import { Icon } from '@iconify/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { api, fmtRelative } from '../api';
import { notificationTitle, t } from '../i18n';
import { get, invalidate } from '../query';
import {
  type Inbox,
  NOTIFICATIONS_KEY,
  NOTIFICATIONS_POLL_MS,
  type NotificationRow,
} from './notifications';
import { errorMessage } from './ui/ErrorState';

// Notification rows have been written by every domain writer since day one,
// but until now nothing read them back — delivery was web-push only. If the
// browser was closed, or push was never granted, "Yossi logged 3h overtime"
// simply never reached anyone. This is the in-app half.

/** How many rows the dropdown shows. The fetch is the whole inbox, shared with
 *  the notifications screen; this is only how much of it fits in a dropdown. */
const SHOWN = 30;

export function NotificationBell({ variant = 'default' }: { variant?: 'default' | 'chip' }) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const panelRef = useRef<HTMLDivElement | null>(null);
  // The sheet is portalled out of this subtree, so "outside" has to be told
  // about it explicitly — otherwise tapping a notification counts as a click
  // outside the panel and closes the thing you just tapped.
  const sheetRef = useRef<HTMLDivElement | null>(null);

  // One cache entry for the whole inbox. This used to be a setInterval outside
  // the cache, running its own fetch of a second URL alongside the one the
  // notifications screen made — two poll loops, two unread counts, and the
  // bell went on polling in a tab nobody had looked at in a day. React Query
  // holds the interval while the window is in the background.
  const inbox = useQuery({
    queryKey: [NOTIFICATIONS_KEY],
    queryFn: get<Inbox>,
    refetchInterval: NOTIFICATIONS_POLL_MS,
  });
  const items = (inbox.data?.items ?? []).slice(0, SHOWN);
  const unread = inbox.data?.unread ?? 0;
  const err = inbox.isError ? errorMessage(inbox.error) : null;

  // Close on outside click and on Escape — a dropdown that traps you is worse
  // than no dropdown.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const target = e.target as Node;
      const inBell = panelRef.current?.contains(target);
      const inSheet = sheetRef.current?.contains(target);
      if (!inBell && !inSheet) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const markAll = useMutation({
    mutationFn: () =>
      api('/v1/notifications/read-all', { method: 'POST', body: JSON.stringify({}) }),
    onSettled: () => invalidate('/v1/notifications'),
  });

  const markRead = useMutation({
    mutationFn: (id: string) => api(`/v1/notifications/${id}/read`, { method: 'POST', body: '{}' }),
    // The dot goes out under the thumb; the refetch below confirms it. A
    // failure is non-critical — the row simply stays unread.
    onMutate: (id) => {
      qc.setQueryData<Inbox>([NOTIFICATIONS_KEY], (old) =>
        old
          ? {
              items: old.items.map((n) =>
                n.id === id ? { ...n, readAt: new Date().toISOString() } : n,
              ),
              unread: Math.max(0, old.unread - 1),
            }
          : old,
      );
    },
    onSettled: () => invalidate('/v1/notifications'),
  });

  function openPanel() {
    setOpen((v) => !v);
    if (!open) void inbox.refetch();
  }

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={openPanel}
        aria-label={unread > 0 ? `${t('Notifications')} (${unread})` : t('Notifications')}
        aria-expanded={open}
        className={
          variant === 'chip'
            ? 'relative flex h-12 w-12 items-center justify-center rounded-[20px] border border-border bg-card text-foreground shadow-[0_4px_12px_rgba(0,0,0,0.04)] transition-all active:scale-95'
            : 'touch relative inline-flex items-center justify-center rounded-md px-3 text-sm text-foreground/80 hover:text-foreground'
        }
      >
        {variant === 'chip' ? (
          <Icon icon="solar:bell-bing-bold" className="text-2xl opacity-80" aria-hidden />
        ) : (
          <BellIcon />
        )}
        {unread > 0 &&
          (variant === 'chip' ? (
            <span className="absolute end-3.5 top-3.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-primary" />
          ) : (
            <span className="absolute end-1.5 top-1 min-w-[1.15rem] rounded-full bg-danger px-1 text-[10px] font-semibold leading-[1.15rem] text-white">
              {unread > 9 ? '9+' : unread}
            </span>
          ))}
      </button>

      {/* Rendered into the body, not here.
          On a phone this is a sheet that comes up from the bottom rather than
          a dropdown hanging off a bell in the corner — a panel anchored to a
          header has nowhere to go on a 390px screen, and the last lines were
          simply unreachable. And it has to leave this subtree to do it: the
          app header is blurred, and a blurred element becomes the frame that
          `fixed` positions against, so the sheet was pinning itself to the
          header and sliding off the top of the screen. */}
      {open &&
        createPortal(
          <>
            {/* biome-ignore lint/a11y/useKeyWithClickEvents: this is an aria-hidden, non-focusable scrim whose only job is a tap-outside dismissal for pointer users; keyboard users already close the sheet with Escape (see the keydown listener above), so a key handler here would be unreachable. */}
            <div
              className="fixed inset-0 z-40 bg-primary/40 sm:bg-transparent"
              aria-hidden="true"
              onClick={() => setOpen(false)}
            />
            <div
              ref={sheetRef}
              // biome-ignore lint/a11y/useSemanticElements: a native <dialog> would have to be driven imperatively by showModal()/close(); this sheet is rendered conditionally by React, and role="dialog" + aria-label announces the same thing without changing the mount/unmount model.
              role="dialog"
              aria-label={t('Notifications')}
              className="fixed inset-x-0 bottom-0 z-50 max-h-[80dvh] overflow-y-auto rounded-t-2xl border border-border bg-white shadow-[0_-8px_40px_rgba(14,31,54,0.18)] sm:inset-x-auto sm:bottom-auto sm:end-4 sm:top-16 sm:max-h-[70vh] sm:w-[22rem] sm:rounded-lg sm:shadow-[0_20px_60px_rgba(14,31,54,0.14)]"
              style={{ paddingBottom: 'var(--safe-bottom)' }}
            >
              {/* Sticky, because the sheet scrolls and the way out of a sheet must
              never be the thing that scrolls away. */}
              <div className="sticky top-0 flex items-center justify-between border-b border-border bg-white px-4 py-3">
                <span className="text-sm font-semibold">{t('Notifications')}</span>
                {unread > 0 && (
                  <button
                    type="button"
                    className="text-xs text-muted-foreground underline"
                    onClick={() => markAll.mutate()}
                  >
                    {t('Mark all read')}
                  </button>
                )}
              </div>

              {err && (
                <div className="flex items-center justify-between gap-2 px-4 py-3 text-sm text-danger">
                  <span>{err}</span>
                  <button
                    type="button"
                    onClick={() => void inbox.refetch()}
                    className="shrink-0 text-xs font-semibold underline"
                  >
                    {t('Try again')}
                  </button>
                </div>
              )}

              {items.length === 0 && !err ? (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                  {t('Nothing new.')}
                </div>
              ) : (
                <ul>
                  {items.map((n) => (
                    <li key={n.id}>
                      <button
                        type="button"
                        onClick={() => !n.readAt && markRead.mutate(n.id)}
                        className={`flex w-full flex-col items-start gap-0.5 border-b border-border px-4 py-3 text-start last:border-b-0 ${
                          n.readAt ? 'opacity-60' : 'bg-muted/40'
                        }`}
                      >
                        {/* The title is written by the server in English — either a
                        short fixed string or a fixed template with slots for a name,
                        an hour count, or a payroll run label — so it is translated
                        and filled in here rather than making every writer carry four
                        languages. */}
                        <span className="text-sm font-medium">{notificationTitle(n)}</span>
                        <span className="text-xs text-muted-foreground">{n.body}</span>
                        <span className="text-[11px] text-muted-foreground">
                          {fmtRelative(n.createdAt)}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}

function BellIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}
